import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { SignInCancelled, fetchCurrentUser, signInWithGoogle, signOut as endSession } from '../services/auth'
import { readToken, setSessionExpiredHandler } from '../services/client'
import { disablePush, syncPush } from '../services/pushService'
import { forgetDestination } from '../utils/returnTo'

// The same shape and signature the partner console's AuthContext has, minus the role
// gate: every account that can sign in with Google holds `user`, so "signed in" is the
// whole of the guard here. There is no wrong-account screen because there is no wrong
// account.

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  /**
   * Seeded from storage rather than defaulted to `true`.
   *
   * With a stored token there is a session to restore, and rendering the sign-in screen
   * for a tenth of a second before replacing it is a flash the customer sees on every
   * reload. With no token there is nothing to wait for, and starting at `true` would mean
   * setting it false inside the effect — a cascading render.
   */
  const [loading, setLoading] = useState(() => Boolean(readToken()))
  const [signingIn, setSigningIn] = useState(false)
  const [error, setError] = useState(null)

  // Restore on load. The access token is very likely expired — a customer orders lunch on
  // Tuesday and opens the app again on Friday — so this call is expected to 401 and be
  // rescued by the client's refresh. Only a dead refresh token fails for real.
  useEffect(() => {
    let current = true

    if (!readToken()) return () => {}

    fetchCurrentUser()
      .then((account) => current && setUser(account))
      .catch(() => current && setUser(null))
      .finally(() => current && setLoading(false))

    return () => {
      current = false
    }
  }, [])

  // A session can end inside a call this context never made — an order list refreshing
  // while the customer reads their receipt. Without this the app keeps rendering a
  // signed-in shell around endpoints that all answer 401.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null)
      setError('Your session has ended. Please sign in again.')
    })

    return () => setSessionExpiredHandler(null)
  }, [])

  /**
   * Re-tell the server which browser this is, every time an account is signed in here.
   *
   * The subscription used to be sent exactly once — from the click that granted permission
   * — and never again, so an FCM token that rotated left the server holding a dead one and
   * nothing to replace it with. Notifications stopped, and the only screen that could have
   * fixed it hides its button once permission is granted. `syncPush` is the repair, and it
   * belongs here because the endpoint identifies the account by the caller's session: this
   * is the one place that knows there is one.
   *
   * Keyed on `uid` rather than the object, so restoring a session does not re-send on every
   * render — and so signing in as somebody else *does*, which is what moves the token onto
   * the account now using the browser.
   *
   * Unawaited and unhandled: it does nothing at all unless notifications were already
   * allowed, and a customer signing in must not wait on a background registration.
   */
  useEffect(() => {
    if (!user?.uid) return

    syncPush()
  }, [user?.uid])

  const signIn = useCallback(async () => {
    setError(null)
    setSigningIn(true)

    try {
      setUser(await signInWithGoogle())
    } catch (failure) {
      // Closing the popup is not an error worth a red line under the button.
      if (!(failure instanceof SignInCancelled)) setError(failure.message)
    } finally {
      setSigningIn(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    // Before, not after. The endpoint works out which subscription to drop from the
    // caller's own session, and once that is gone there is nothing left to name it with —
    // the token would stay attached to this account and the next person to use the phone
    // would get somebody else's order notifications. Awaited for the same reason, and it
    // never throws, so it cannot hold up a sign-out.
    await disablePush()

    await endSession()
    setUser(null)
    setError(null)
    // Leaving on purpose is not the same as being stopped at the gate. Without this, the
    // next person to sign in on this phone is thrown at the last screen the previous one
    // was blocked from — an order form that is not theirs.
    forgetDestination()
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signingIn, error, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
